"use client"
import { forwardRef, type ComponentPropsWithoutRef } from "react"
import { useMetricTrigger } from "./useMetricTrigger"
import type { MetricId } from "./metricDetailsTypes"
import {
  METRIC_TRIGGER_RED_CHIP_STYLE,
  useMetricHoverSwap,
  type HoverableMetricId,
} from "../blocks/metrics/metricHoverSwap"

// The details-panel MetricId and the hover-swap HoverableMetricId disagree
// for two of the four stat-row metrics ("income" vs "medianIncome",
// "alignment" vs "alignmentEffort") — translate before comparing, or the
// chip never lights on those two. Triggers whose id is NOT in this dict
// (the labor strip, the treemap, the graph) are not stat-row metrics and
// must never chip — an unmapped id yields undefined, which would otherwise
// equal the idle hoveredMetric?.id and light them permanently red.
const HOVERABLE_METRIC_ID_BY_TRIGGER_ID: Partial<
  Record<MetricId, HoverableMetricId>
> = {
  employment: "employment",
  income: "medianIncome",
  alignment: "alignmentEffort",
  safetyTax: "safetyTax",
}

// The clickable trigger every HUD metric block shares: the shared trigger
// props from useMetricTrigger (role/aria/click/keyboard, flight origin
// capture). Forwards ref and extra props so it can sit directly under a
// Tooltip (which cloneElements hover handlers onto its child).
//
// Inside a <MetricHoverSwap triggerHighlight="red-chip">, the trigger whose
// metric is currently hovered paints METRIC_TRIGGER_RED_CHIP_STYLE behind
// itself: a rounded red square with white text. The padding that gives the
// chip its breathing room is applied whenever the treatment is on, so nothing
// shifts when the hover lands — only the background and text color flip.
// Vertical padding is REAL (the stat row is that little bit taller and the
// chips sit inside it); horizontal padding is cancelled by negative margins
// so the outer figures stay flush with the policy column's edges. The stat
// row stretches its items (see the tab-3 row in variants.tsx), so every
// chip is the row's full height regardless of how many lines its label takes.
export const MetricTrigger = forwardRef<
  HTMLDivElement,
  { id: MetricId } & ComponentPropsWithoutRef<"div">
>(({ id, className, style, children, ...rest }, ref) => {
  const metricSwap = useMetricHoverSwap()
  const hoverableId = HOVERABLE_METRIC_ID_BY_TRIGGER_ID[id]
  const chipTreatment =
    metricSwap?.triggerHighlight === "red-chip" && hoverableId !== undefined
  const chipLit =
    chipTreatment && metricSwap?.hoveredMetric?.id === hoverableId
  const chip = METRIC_TRIGGER_RED_CHIP_STYLE
  return (
    <div
      ref={ref}
      className={`cursor-pointer ${className ?? ""}`}
      {...useMetricTrigger(id)}
      {...rest}
      style={{
        ...(chipTreatment && {
          padding: `${chip.paddingYPx}px ${chip.paddingXPx}px`,
          marginLeft: -chip.paddingXPx,
          marginRight: -chip.paddingXPx,
          borderRadius: chip.borderRadiusPx,
        }),
        ...(chipLit && {
          background: chip.background,
          color: chip.textColor,
        }),
        ...style,
      }}
    >
      {children}
    </div>
  )
})
MetricTrigger.displayName = "MetricTrigger"
